/**
 * Total Reconciliation Banner.
 *
 * Renders the backend-computed comparison between:
 *  - the total hours the OCR read off the sheet's "TOTAL" line
 *  - the independently-calculated sum of each daily entry the backend
 *    performed (see backend/services/total_reconciliation.py)
 *
 * The backend is the source of truth. This component is a pure
 * display of the ``ts.total_reconciliation`` structured field —
 * it does NOT recompute anything client-side so a discrepancy the
 * operator sees is EXACTLY what the audit log says.
 *
 * Three visual states:
 *   1. Verified (green): stated total matches calculated within
 *      ±0.25 h. Confidence pill, no action needed. Rendered as
 *      a compact banner so it doesn't nag.
 *   2. Discrepancy (amber): totals disagree. Full side-by-side
 *      breakdown so the operator can spot the wrong entry at a
 *      glance without opening the scan lightbox.
 *   3. No stated total (slate): OCR couldn't read a TOTAL from
 *      the sheet. Neutral message — the calculated sum is used
 *      downstream anyway.
 *
 * Also honours the ``total_reconciliation.status === 'verified'``
 * as an accessibility signal (aria-label carries the status).
 */
import { CheckCircle, Warning, ClockCounterClockwise } from '@phosphor-icons/react';

const fmt = (h) =>
  h == null ? '—' : `${Number(h).toFixed(2).replace(/\.00$/, '.0')} h`;

const TotalReconciliationBanner = ({ reconciliation, tsId }) => {
  if (!reconciliation) return null;

  const {
    stated_total_hours,
    calculated_total_hours,
    discrepancy_hours,
    discrepancy_pct,
    status,
    message,
  } = reconciliation;

  // ── Verified ──────────────────────────────────────────────
  if (status === 'verified') {
    return (
      <div
        className="mx-5 mt-3 mb-1 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800"
        role="status"
        aria-label={`Totals verified — sheet ${fmt(stated_total_hours)} matches calculated ${fmt(calculated_total_hours)}`}
        data-testid={`reconciliation-verified-${tsId}`}
      >
        <CheckCircle size={16} weight="fill" className="shrink-0 text-emerald-600" />
        <span className="font-medium">Totals verified</span>
        <span className="text-emerald-700/80">
          — sheet {fmt(stated_total_hours)} matches calculated {fmt(calculated_total_hours)}
        </span>
      </div>
    );
  }

  // ── No stated total on sheet ──────────────────────────────
  if (status === 'no_stated_total') {
    return (
      <div
        className="mx-5 mt-3 mb-1 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700"
        role="status"
        aria-label="No stated total on sheet"
        data-testid={`reconciliation-neutral-${tsId}`}
      >
        <ClockCounterClockwise size={16} className="shrink-0 text-slate-500" />
        <span>
          No weekly total on sheet — using calculated {fmt(calculated_total_hours)}.
        </span>
      </div>
    );
  }

  // ── Mismatch ──────────────────────────────────────────────
  const pctLabel =
    discrepancy_pct == null
      ? ''
      : ` (${discrepancy_pct > 0 ? '+' : ''}${discrepancy_pct.toFixed(1)}%)`;
  const direction = discrepancy_hours > 0 ? 'over' : 'short';

  return (
    <div
      className="mx-5 mt-3 mb-1 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs"
      role="alert"
      aria-label={`Total mismatch — pending review`}
      data-testid={`reconciliation-mismatch-${tsId}`}
    >
      <div className="flex items-center gap-2 text-amber-900 font-semibold mb-1.5">
        <Warning size={16} weight="fill" className="shrink-0 text-amber-600" />
        <span>Total mismatch — pending review</span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px]">
        <div className="rounded bg-white border border-amber-200 px-2 py-1.5">
          <div className="uppercase text-[9px] tracking-wide text-amber-700 mb-0.5">
            Sheet total
          </div>
          <div className="font-mono font-bold text-amber-900" data-testid={`reconciliation-stated-${tsId}`}>
            {fmt(stated_total_hours)}
          </div>
        </div>
        <div className="rounded bg-white border border-amber-200 px-2 py-1.5">
          <div className="uppercase text-[9px] tracking-wide text-amber-700 mb-0.5">
            Calculated
          </div>
          <div className="font-mono font-bold text-amber-900" data-testid={`reconciliation-calculated-${tsId}`}>
            {fmt(calculated_total_hours)}
          </div>
        </div>
        <div className="rounded bg-white border border-amber-200 px-2 py-1.5">
          <div className="uppercase text-[9px] tracking-wide text-amber-700 mb-0.5">
            Off by
          </div>
          <div className="font-mono font-bold text-amber-900" data-testid={`reconciliation-discrepancy-${tsId}`}>
            {fmt(Math.abs(discrepancy_hours))}
            <span className="ml-1 font-normal text-amber-700 text-[10px]">
              {direction}
              {pctLabel}
            </span>
          </div>
        </div>
      </div>
      <p className="mt-2 text-amber-800 leading-snug">{message}</p>
    </div>
  );
};

export default TotalReconciliationBanner;
